import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, message, Table, Space, Popconfirm, Tag, Progress, Modal, Radio, Alert } from 'antd';
import { ArrowLeftOutlined, ExperimentOutlined, EyeOutlined, DeleteOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { createTask, getTasksWithCurrent, deleteTask, cancelTask, TaskResponse, shouldDisplayInList } from '@/utils/taskManager';
import { isProfileVerifyRequired } from '@/api/taskManagerApi';
import MolStarViewer from '@/components/results/protein/structure/MolStarViewer';
import { formatUTCToLocal } from '@/utils/timeUtils';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

export default function ProtenixPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedResult, setSelectedResult] = useState<{
    title: string;
    data: any;
    taskResult: TaskResponse;
    batchResults?: any[];  // 批量结果数组
  } | null>(null);
  const [resultModalVisible, setResultModalVisible] = useState(false);

  // 加载任务列表
  const loadTasks = async () => {
    setLoading(true);
    try {
      const taskList = await getTasksWithCurrent(0, 100, undefined, 'structure_prediction', undefined, 'protenix_predict_json');
      
      const deletedTaskIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      const finalTasks = taskList.filter(task => !deletedTaskIds.includes(task.id));
      const displayTasks = finalTasks.filter(shouldDisplayInList);
      
      setTasks(displayTasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
    } catch (error) {
      console.error('加载任务列表失败:', error);
      if (!isProfileVerifyRequired(error)) {
        message.error('加载任务列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // FASTA格式验证
  const validateFasta = (fastaContent: string): { valid: boolean; error?: string; count?: number } => {
    if (!fastaContent || !fastaContent.trim()) {
      return { valid: false, error: '请输入FASTA格式序列' };
    }

    const lines = fastaContent.trim().split('\n');
    const headers = lines.filter(line => line.trim().startsWith('>'));
    
    if (headers.length === 0) {
      return { valid: false, error: 'FASTA格式错误：未找到序列标识符（以>开头的行）' };
    }

    if (headers.length > 10) {
      return { valid: false, error: `序列数量超过限制：最多支持10条序列，当前有${headers.length}条` };
    }

    // 检查每个序列是否有内容
    let currentSeq = '';
    let seqCount = 0;
    for (const line of lines) {
      if (line.trim().startsWith('>')) {
        if (currentSeq && currentSeq.trim()) {
          seqCount++;
        }
        currentSeq = '';
      } else {
        currentSeq += line.trim();
      }
    }
    if (currentSeq && currentSeq.trim()) {
      seqCount++;
    }

    if (seqCount === 0) {
      return { valid: false, error: 'FASTA格式错误：未找到有效的序列内容' };
    }

    return { valid: true, count: seqCount };
  };

  // 提交预测任务
  const handleSubmit = async (values: any) => {
    // 验证FASTA格式
    const validation = validateFasta(values.fasta_content);
    if (!validation.valid) {
      message.error(validation.error);
      return;
    }

    // 如果是单条序列，强制使用单体模式
    let predictionMode = values.prediction_mode;
    if (validation.count === 1) {
      predictionMode = 'monomer';
    }

    setSubmitting(true);
    try {
      const modeText = predictionMode === 'monomer' ? '单体' : '复合物';
      const taskData = {
        name: values.name || `Protenix${modeText}预测`,
        description: `Protenix蛋白质${modeText}结构预测 - ${values.name || 'protein'} (${validation.count}条序列)`,
        service_name: 'structure_prediction',
          task_type: 'protenix_prediction',
        handler_name: 'protenix_predict_json',
        parameters: {
          fasta_content: values.fasta_content.trim(),
          prediction_mode: predictionMode,
          name: values.name || 'protein'
        },
        priority: 1,
        timeout: 3600,
        max_retries: 1
      };

      const task = await createTask(taskData);
      message.success('任务已提交，正在处理中...');
      setTasks(previous => [task, ...previous.filter(item => item.id !== task.id)]);
      form.resetFields();
      loadTasks();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || '提交任务失败';
      console.error('提交任务失败:', error);
      message.error(`提交任务失败: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      const deletedTasks = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      if (!deletedTasks.includes(taskId)) {
        deletedTasks.push(taskId);
        localStorage.setItem('deletedTaskIds', JSON.stringify(deletedTasks));
      }
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      message.success('任务已删除');
    } catch (error) {
      console.error('删除任务失败:', error);
      message.error('删除任务失败');
    }
  };

  const handleCancel = async (taskId: string) => {
    try {
      await cancelTask(taskId);
      message.success('任务已取消');
      loadTasks();
    } catch (error) {
      console.error('取消任务失败:', error);
      message.error('取消任务失败');
    }
  };

  const handleViewResult = (task: TaskResponse) => {
    if (task.result) {
      console.log('========== Protenix 结果解析 ==========');
      console.log('任务结果:', JSON.stringify(task.result, null, 2));
      console.log('任务参数:', JSON.stringify(task.parameters, null, 2));
      
      // 检查 samples 和 confidence_scores
      if (task.result.results && task.result.results[0]) {
        const firstResult = task.result.results[0].result;
        console.log('第一个结果的 keys:', Object.keys(firstResult || {}));
        console.log('samples:', firstResult?.samples);
        console.log('confidence_scores:', firstResult?.confidence_scores);
      }
      
      // 判断是单体模式还是复合物模式
      const predictionMode = task.parameters?.prediction_mode || 'complex';
      const isBatchMode = predictionMode === 'monomer' && task.result.total_sequences > 1;
      
      console.log('预测模式:', predictionMode);
      console.log('是否批量模式:', isBatchMode);
      console.log('total_sequences:', task.result.total_sequences);
      
      // 检查是否是批量结果（单体模式且多个序列）
      if (isBatchMode && task.result.results && Array.isArray(task.result.results)) {
        console.log('批量结果（单体模式）:', task.result.results);
        // 批量结果：显示第一个成功的结果
        const successResult = task.result.results.find((r: any) => {
          const hasContent = r.result?.pdb_content && r.result.pdb_content.length > 0;
          console.log(`检查序列 ${r.sequence_id}:`, {
            success: r.success,
            hasResult: !!r.result,
            hasPdbContent: hasContent
          });
          return r.success && hasContent;
        });
        
        if (successResult) {
          setSelectedResult({
            title: `${successResult.result.name || successResult.sequence_id} - 预测结果`,
            data: successResult.result,
            taskResult: task,
            batchResults: task.result.results
          });
          setResultModalVisible(true);
        } else {
          console.error('未找到成功的结果，所有结果:', task.result.results);
          message.warning('所有序列预测均失败');
        }
      } else {
        // 单个结果（复合物模式或单条序列）
        console.log('进入单个结果处理分支');
        console.log('原始结果:', task.result);
        
        // 复合物模式可能也被包装在 results 数组中
        let resultData = task.result;
        
        if (task.result.results && Array.isArray(task.result.results)) {
          console.log('发现 results 数组，长度:', task.result.results.length);
          console.log('results[0]:', JSON.stringify(task.result.results[0], null, 2));
          
          if (task.result.results.length === 1) {
            // 如果有 results 数组但只有一个元素，提取出来
            const singleResult = task.result.results[0];
            console.log('提取单个结果:', singleResult);
            
            if (singleResult.result) {
              resultData = singleResult.result;
              console.log('使用 singleResult.result:', resultData);
            } else {
              console.log('singleResult 没有 result 字段，使用整个 singleResult');
              resultData = singleResult;
            }
          }
        }
        
        console.log('最终 resultData:', JSON.stringify(resultData, null, 2));
        console.log('pdb_content 存在:', !!resultData.pdb_content);
        console.log('pdb_content 长度:', resultData.pdb_content?.length);
        
        // 检查是否有有效的结构数据
        if (resultData.pdb_content && resultData.pdb_content.length > 0) {
          console.log('✓ 找到有效的结构数据，显示结果');
          setSelectedResult({
            title: `${resultData.name || task.name} - 预测结果`,
            data: resultData,
            taskResult: task
          });
          setResultModalVisible(true);
        } else {
          console.error('✗ 结果中没有有效的结构数据');
          console.error('resultData 完整内容:', resultData);
          message.warning('预测结果中没有结构数据');
        }
      }
    } else {
      message.warning('暂无结果数据');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      'pending': { color: 'blue', text: '等待中' },
      'running': { color: 'orange', text: '运行中' },
      'completed': { color: 'green', text: '已完成' },
      'failed': { color: 'red', text: '失败' },
      'cancelled': { color: 'gray', text: '已取消' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number, record: TaskResponse) => {
        if (record.status === 'completed') return <Progress percent={100} size="small" />;
        if (record.status === 'failed' || record.status === 'cancelled') return <Progress percent={0} status="exception" size="small" />;
        return <Progress percent={progress || 0} size="small" />;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => formatUTCToLocal(time),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: TaskResponse) => (
        <Space size="middle">
          {record.status === 'completed' && (
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              onClick={() => handleViewResult(record)}
            >
              查看结果
            </Button>
          )}
          {(record.status === 'pending' || record.status === 'running') && (
            <Button 
              type="link" 
              onClick={() => handleCancel(record.id)}
            >
              取消
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个任务吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/structure')}
          style={{ marginBottom: 16 }}
        >
          返回结构预测
        </Button>
        
        <Typography>
          <Title level={2}>Protenix 蛋白质结构预测</Title>
          <Paragraph>
            使用 <Text strong>protenix_mini_esm_v0.5.0</Text> 模型预测蛋白质单体或复合物的三维结构。
          </Paragraph>
        </Typography>
      </div>

      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="结构名称"
            initialValue="protein"
          >
            <Input placeholder="输入结构名称" />
          </Form.Item>

          <Form.Item
            name="fasta_content"
            label="FASTA格式序列"
            rules={[{ required: true, message: '请输入FASTA格式序列' }]}
          >
            <TextArea 
              rows={12}
              placeholder={`FASTA格式说明（支持1-10条序列）：
• 每条序列以 > 开头作为标识符
• 单体模式：每条序列单独预测
• 复合物模式：所有序列作为一个复合物预测
• 无需MSA，快速预测
• 预测时间较长（数分钟到数十分钟），请耐心等待

示例：
>Protein1
MKTFAKLVGSHQRT...
>Protein2
AKLVMKTFGSHQRT...`}
            />
          </Form.Item>

          <Form.Item
            name="prediction_mode"
            label="预测模式"
            initialValue="complex"
            extra="单条序列将自动使用单体模式"
          >
            <Radio.Group>
              <Radio value="monomer">单体预测（每条序列单独预测）</Radio>
              <Radio value="complex">复合物预测（所有序列作为一个复合物）</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={submitting}
              icon={<ExperimentOutlined />}
            >
              开始预测
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card 
        title="任务列表" 
        style={{ marginTop: 24 }}
        extra={
          <Button 
            size="small" 
            onClick={loadTasks}
            loading={loading}
            icon={<SyncOutlined />}
          >
            刷新任务
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个任务`,
          }}
        />
      </Card>

      <Modal
        title={
          <div>
            <span>Protenix 结构预测结果</span>
            {selectedResult?.batchResults && selectedResult.batchResults.length > 1 && (
              <span style={{ marginLeft: 16, fontSize: 14, color: '#666' }}>
                (共 {selectedResult.batchResults.length} 个序列)
              </span>
            )}
          </div>
        }
        open={!!selectedResult}
        onCancel={() => setSelectedResult(null)}
        footer={null}
        width={1200}
        centered
        bodyStyle={{ padding: 0, height: '80vh', overflow: 'hidden' }}
      >
        {selectedResult && (
          <div style={{ 
            width: '100%', 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* 批量结果选择器（单体模式多序列） */}
            {selectedResult.batchResults && selectedResult.batchResults.length > 1 && (
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ marginRight: 8 }}>选择序列：</span>
                  {selectedResult.batchResults.map((batchItem: any, index: number) => {
                    const itemName = batchItem.result?.name || batchItem.sequence_id;
                    const isActive = selectedResult.data.name === itemName;
                    const hasContent = batchItem.result?.pdb_content && batchItem.result.pdb_content.length > 0;
                    
                    return (
                      <Button
                        key={index}
                        size="small"
                        type={isActive ? 'primary' : 'default'}
                        onClick={() => {
                          setSelectedResult({
                            ...selectedResult,
                            title: `${itemName} - 预测结果`,
                            data: batchItem.result
                          });
                        }}
                        style={{ marginRight: 8 }}
                        disabled={!batchItem.success || !hasContent}
                      >
                        {itemName} {batchItem.success && hasContent ? '✓' : '✗'}
                      </Button>
                    );
                  })}
                </div>
                {selectedResult?.data?.job_id && (
                  <Button 
                    type="primary"
                    size="small"
                    onClick={() => {
                      const jobId = selectedResult.data.job_id.split('_')[0];
                      const a = document.createElement('a');
                      // 使用相对路径，自动适配当前域名和端口
                      a.href = `/api/structure_prediction/download_results/${jobId}`;
                      a.download = `protenix_results_${jobId}.zip`;
                      a.style.display = 'none';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                  >
                    下载所有结果
                  </Button>
                )}
              </div>
            )}
            
            {/* 复合物模式或单序列模式的下载按钮 */}
            {(!selectedResult.batchResults || selectedResult.batchResults.length <= 1) && selectedResult?.data?.job_id && (
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  type="primary"
                  size="small"
                  onClick={() => {
                    const jobId = selectedResult.data.job_id.split('_')[0];
                    const a = document.createElement('a');
                    // 使用相对路径，自动适配当前域名和端口
                    a.href = `/api/structure_prediction/download_results/${jobId}`;
                    a.download = `protenix_results_${jobId}.zip`;
                    a.style.display = 'none';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                >
                  下载所有结果
                </Button>
              </div>
            )}
            
            {/* 样本选择器（5个预测结构） */}
            {selectedResult.data?.samples && selectedResult.data.samples.length > 1 && (
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                <span style={{ marginRight: 8 }}>选择样本：</span>
                {selectedResult.data.samples.map((sample: any, index: number) => {
                  const isActive = selectedResult.data.structure_content === sample.structure_content;
                  
                  return (
                    <Button
                      key={index}
                      size="small"
                      type={isActive ? 'primary' : 'default'}
                      onClick={() => {
                        // 切换到选中的样本
                        setSelectedResult({
                          ...selectedResult,
                          data: {
                            ...selectedResult.data,
                            structure_content: sample.structure_content,
                            pdb_content: sample.structure_content,
                            confidence_scores: sample.confidence_scores,
                            structure_file: sample.structure_file
                          }
                        });
                      }}
                      style={{ marginRight: 8 }}
                    >
                      Sample {sample.sample_id}
                    </Button>
                  );
                })}
              </div>
            )}
            
            {/* 置信度分数显示 */}
            {(() => {
              console.log('置信度分数检查:', {
                hasConfidenceScores: !!selectedResult.data?.confidence_scores,
                confidenceScores: selectedResult.data?.confidence_scores,
                data: selectedResult.data
              });
              return selectedResult.data?.confidence_scores && (
                <div style={{ padding: '12px 16px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {selectedResult.data.confidence_scores.plddt !== undefined && (
                      <span><strong>pLDDT:</strong> {selectedResult.data.confidence_scores.plddt.toFixed(2)}</span>
                    )}
                    {selectedResult.data.confidence_scores.gpde !== undefined && (
                      <span><strong>GPDE:</strong> {selectedResult.data.confidence_scores.gpde.toFixed(3)}</span>
                    )}
                    {selectedResult.data.confidence_scores.ptm !== undefined && (
                      <span><strong>pTM:</strong> {selectedResult.data.confidence_scores.ptm.toFixed(3)}</span>
                    )}
                    {selectedResult.data.confidence_scores.iptm !== undefined && selectedResult.data.confidence_scores.iptm > 0 && (
                      <span><strong>ipTM:</strong> {selectedResult.data.confidence_scores.iptm.toFixed(3)}</span>
                    )}
                    {selectedResult.data.confidence_scores.ranking_score !== undefined && (
                      <span><strong>Ranking Score:</strong> {selectedResult.data.confidence_scores.ranking_score.toFixed(4)}</span>
                    )}
                  </div>
                </div>
              );
            })()}
            
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <MolStarViewer
                pdbData={selectedResult.data?.pdb_content}
                title={selectedResult.title}
                height="auto"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
